import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  TextField,
  Divider,
  Paper,
  Chip,
  Breadcrumbs,
  Link,
  Alert,
  List,
  ListItem,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  Add,
  Remove,
  Delete,
  ShoppingCartCheckout,
  Home,
  ShoppingCart,
  LocalShipping,
  Security,
  ArrowForward,
  CreditCard,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import {
  addToCart,
  updateCartItemQuantity,
  clearCart,
  applyCoupon,
  removeCoupon,
  selectCartItems,
  selectCartTotals,
  selectCartItemCount,
  // New selection imports
  toggleItemSelection,
  selectAllItems,
  deselectAllItems,
  selectSelectedItems,
  selectSelectedCartItems,
  selectSelectedItemsCount,
  selectIsItemSelected,
  selectAllItemsSelected,
  loadAvailablePromotions,
  updateLotBookingLotCount,
  updateCartItemProductInfo,
  getCartItemAmount,
  getCartItemSelectedSft,
  fetchCustomerCart,
  removeCartItem,
} from '../store/slices/cartSlice';
import { getFullImageUrl } from '../utils/imageUtils';
import { productService } from '../services/productService';
import { getMinOrderSlabs, getProductLots } from '../utils/lotUtils';

const CartPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  const cartItems = useSelector(selectCartItems);
  const cartTotals = useSelector(selectCartTotals);
  const itemCount = useSelector(selectCartItemCount);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const availablePromotions = useSelector((state) => state.cart.availablePromotions);
  // Get products from Redux for category-based promotion checking
  const { products: allProducts } = useSelector((state) => state.products);
  
  // Selection state
  const selectedItems = useSelector(selectSelectedItems);
  const selectedCartItems = useSelector(selectSelectedCartItems);
  const selectedItemsCount = useSelector(selectSelectedItemsCount);
  const allItemsSelected = useSelector(selectAllItemsSelected);
  
  const [couponCode, setCouponCode] = React.useState('');
  const [couponError, setCouponError] = React.useState('');
  
  // Calculate selected items totals
  const selectedSubtotal = React.useMemo(() => {
    return selectedCartItems.reduce((total, item) => {
      return total + getCartItemAmount(item);
    }, 0);
  }, [selectedCartItems]);
  
  const selectedGst = selectedSubtotal * 0.18; // 18% GST
  const selectedSubtotalWithGst = selectedSubtotal + selectedGst;
  // Check if any selected item is a lot booking - exclude shipping for lot booking items
  const hasLotBookingItems = selectedCartItems.some(item => item.isLotBooking || item.isAdvanceBooking);
  const selectedShipping = hasLotBookingItems ? 0 : 200; // No shipping for lot booking, otherwise ₹200
  
  // Calculate discount for selected items only
  const selectedItemsDiscount = React.useMemo(() => {
    if (!cartTotals.couponCode || !cartTotals.discount || cartTotals.discount <= 0) {
      return 0;
    }
    
    // If promotion applies to specific products, calculate discount only for those products in selected items
    const discountMeta = cartTotals.discountMeta;
    if (discountMeta && discountMeta.productIds && discountMeta.productIds.length > 0) {
      const promoProductIds = discountMeta.productIds.map(pid => String(pid));
      const applicableSelectedItems = selectedCartItems.filter(item => 
        promoProductIds.includes(String(item.id ?? item._id))
      );
      
      if (applicableSelectedItems.length === 0) {
        return 0;
      }
      
      // Calculate subtotal for applicable selected items
      const applicableSubtotal = applicableSelectedItems.reduce((sum, item) => {
        return sum + getCartItemAmount(item);
      }, 0);
      
      // Calculate discount based on discount type
      if (discountMeta.discountType === 'percentage') {
        return (applicableSubtotal * discountMeta.value) / 100;
      } else if (discountMeta.discountType === 'amount') {
        // For amount discount, use the discount amount directly but ensure it doesn't exceed applicable subtotal
        return Math.min(discountMeta.discountAmount || discountMeta.value || 0, applicableSubtotal);
      } else {
        return discountMeta.value || 0;
      }
    }
    
    // If promotion applies to all items, calculate proportionally based on selected items
    const allItemsSubtotal = cartItems.reduce((sum, item) => {
      return sum + getCartItemAmount(item);
    }, 0);
    
    if (allItemsSubtotal <= 0) {
      return 0;
    }
    
    // Proportional discount for selected items
    return (selectedSubtotal / allItemsSubtotal) * cartTotals.discount;
  }, [selectedCartItems, cartItems, cartTotals]);
  
  const appliedCoupon = cartTotals.couponCode ? { 
    code: cartTotals.couponCode, 
    discount: selectedItemsDiscount,
    discountMeta: cartTotals.discountMeta
  } : null;
  const selectedTotal = selectedSubtotal + selectedGst - selectedItemsDiscount;

  // Fetch per-SFT price and minimum slabs for regular (non-booking) items from the product API,
  // so items added earlier (or loaded from the backend cart) show the actual values.
  const regularItemRefs = JSON.stringify(cartItems
    .filter(i => !i.isLotBooking && !i.isAdvanceBooking && i.id)
    .map(i => [i.itemKey, i.id]));
  const cartItemsRef = React.useRef(cartItems);
  cartItemsRef.current = cartItems;
  React.useEffect(() => {
    let cancelled = false;
    JSON.parse(regularItemRefs).forEach(([itemKey, productId]) => {
      productService.getProductById(productId)
        .then(data => {
          const p = data?.product || data;
          if (cancelled || !p) return;
          const pricePerSft = Number(p.discountedPrice || p.price);
          const minOrderSlabs = getMinOrderSlabs(p);
          dispatch(updateCartItemProductInfo({
            itemKey,
            pricePerSft: Number.isFinite(pricePerSft) && pricePerSft > 0 ? pricePerSft : null,
            minOrderSlabs,
            lots: getProductLots(p),
          }));
          // Raise items that were reduced below the minimum slabs back up to it
          const current = cartItemsRef.current.find(i => i.itemKey === itemKey);
          if (current && current.quantity < minOrderSlabs) {
            dispatch(updateCartItemQuantity({ itemKey, quantity: minOrderSlabs }));
          }
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [regularItemRefs, dispatch]);

  // Load the customer's cart from the backend on every visit / refresh so it matches across devices
  React.useEffect(() => {
    if (isAuthenticated) dispatch(fetchCustomerCart());
  }, [isAuthenticated, dispatch]);

  // Handle advance booking product from navigation state
  React.useEffect(() => {
    if (location.state?.bookingProduct) {
      const bookingProduct = location.state.bookingProduct;
      const quantity = 1;
      dispatch(addToCart({ product: bookingProduct, quantity }));
      
      if (location.state.message) {
        toast.success(location.state.message);
      }
      
      // Clear the location state to prevent re-adding on refresh
      navigate('/cart', { replace: true });
    }
  }, [location.state, dispatch, navigate]);

  React.useEffect(() => {
    dispatch(loadAvailablePromotions());
    
    // Listen for storage changes to reload promotions when they're deleted/updated
    const handleStorageChange = (e) => {
      if (e.key === 'global_promotions' || e.key === null) {
        // Reload promotions when localStorage changes
        dispatch(loadAvailablePromotions());
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event for same-tab updates
    const handlePromotionChange = () => {
      dispatch(loadAvailablePromotions());
    };
    
    window.addEventListener('promotionsUpdated', handlePromotionChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('promotionsUpdated', handlePromotionChange);
    };
  }, [dispatch]);
  
  const handleQuantityChange = (itemKey, newQuantity) => {
    if (newQuantity < 1) {
      dispatch(removeCartItem(itemKey));
      toast.info('Item removed from cart');
    } else {
      dispatch(updateCartItemQuantity({ itemKey, quantity: newQuantity }));
    }
  };
  
  const handleRemoveItem = (itemKey, itemName) => {
    dispatch(removeCartItem(itemKey));
    toast.success(`${itemName} removed from cart`);
  };
  
  const handleClearCart = async () => {
    try {
      await dispatch(clearCart()).unwrap();
      toast.success('Cart cleared successfully');
    } catch (error) {
      toast.error(error || 'Failed to clear cart');
    }
  };
  
  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    // Simulate coupon validation
    const validCoupons = {
      'SAVE10': { discount: 10, discountType: 'percentage' },
      'FLAT500': { discount: 500, discountType: 'fixed' },
      'WELCOME20': { discount: 20, discountType: 'percentage' },
    };
    
    const coupon = validCoupons[couponCode.toUpperCase()];
    
    if (coupon) {
      dispatch(applyCoupon({
        code: couponCode.toUpperCase(),
        discount: coupon.discount,
        discountType: coupon.discountType,
      }));
      toast.success(`Coupon ${couponCode.toUpperCase()} applied successfully!`);
      setCouponCode('');
      setCouponError('');
    } else {
      setCouponError('Invalid coupon code');
    }
  };
  
  const handleRemoveCoupon = () => {
    dispatch(removeCoupon());
    toast.info('Coupon removed');
  };
  
  // Selection handlers
  const handleToggleItemSelection = (itemKey) => {
    dispatch(toggleItemSelection(itemKey));
  };
  
  const handleSelectAll = () => {
    if (allItemsSelected) {
      dispatch(deselectAllItems());
      toast.info('All items deselected');
    } else {
      dispatch(selectAllItems());
      toast.info('All items selected');
    }
  };
  
  const handleCheckout = () => {
    if (selectedItemsCount === 0) {
      toast.warning('Please select at least one item to checkout');
      return;
    }
    
    // Allow both authenticated and guest users to proceed to checkout
    // Pass selected items and discount information to checkout
    navigate('/checkout', { 
      state: { 
        selectedItems: selectedCartItems,
        fromCart: true,
        appliedDiscount: selectedItemsDiscount,
        discountMeta: cartTotals.discountMeta,
        couponCode: cartTotals.couponCode
      } 
    });
  };
  
  const applicablePromotions = React.useMemo(() => {
    const promotionsList = Array.isArray(availablePromotions) ? availablePromotions : [];
    if (promotionsList.length === 0) return [];
    const itemsReference = selectedItemsCount > 0 ? selectedCartItems : cartItems;
    if (itemsReference.length === 0) return [];

    // Promotions are already filtered by loadAvailablePromotionsFromStorage
    // Check if they apply to current cart items by productId OR category
    return promotionsList.filter((promotion) => {
      // Additional safety check: ensure promotion is valid and not deleted
      if (!promotion || (!promotion.id && !promotion._id && !promotion.promotionId)) {
        return false;
      }
      if (!Array.isArray(promotion.productIds) || promotion.productIds.length === 0) {
        return false;
      }
      const promoIds = promotion.productIds.map((pid) => String(pid));
      
      // Check if any cart item matches by productId
      const matchesByProductId = itemsReference.some((item) => 
        promoIds.includes(String(item.id ?? item._id))
      );
      
      if (matchesByProductId) {
        return true;
      }
      
      // Check if any cart item matches by category
      // Get all products in the promotion to check their categories
      const promotionProductCategories = new Set();
      itemsReference.forEach((item) => {
        // Check if any product in the promotion's productIds has the same category as this cart item
        // Since we don't have all products here, we'll check if the promotion has a categories field
        if (promotion.categories && Array.isArray(promotion.categories)) {
          const itemCategory = item.category ? String(item.category).toLowerCase() : null;
          const promotionCategories = promotion.categories.map(cat => String(cat).toLowerCase());
          if (itemCategory && promotionCategories.includes(itemCategory)) {
            promotionProductCategories.add(itemCategory);
          }
        }
      });
      
      // Also check by inferring category from products in promotion
      // If any cart item's category matches products in the promotion, it applies
      if (promotionProductCategories.size > 0) {
        return true;
      }
      
      // Additional check: if cart items have categories that match products in promotion
      // Check if any products in promotion.productIds belong to the same category as cart items
      const cartItemCategories = new Set(
        itemsReference
          .map(item => item.category ? String(item.category).toLowerCase() : null)
          .filter(cat => cat !== null)
      );
      
      // If promotion has a categories field, check direct match
      if (promotion.categories && Array.isArray(promotion.categories)) {
        const promotionCategories = promotion.categories.map(cat => String(cat).toLowerCase());
        const hasMatchingCategory = Array.from(cartItemCategories).some(cartCat => 
          promotionCategories.includes(cartCat)
        );
        if (hasMatchingCategory) {
          return true;
        }
      }
      
      // Check if any products in the promotion's productIds belong to the same category as cart items
      // This handles category-based promotions where categories were expanded to productIds
      if (allProducts && allProducts.length > 0 && cartItemCategories.size > 0) {
        const promotionProducts = allProducts.filter(p => 
          promoIds.includes(String(p.id || p._id))
        );
        const promotionCategories = new Set(
          promotionProducts
            .map(p => p.category ? String(p.category).toLowerCase() : null)
            .filter(cat => cat !== null)
        );
        
        // Check if any cart item category matches any promotion category
        const hasMatchingCategory = Array.from(cartItemCategories).some(cartCat => 
          promotionCategories.has(cartCat)
        );
        if (hasMatchingCategory) {
          return true;
        }
      }
      
      return false;
    });
  }, [availablePromotions, selectedItemsCount, selectedCartItems, cartItems, allProducts]);

  const handleApplyPromotion = (promotion) => {
    const targetItems = selectedItemsCount > 0 ? selectedCartItems : cartItems;
    const promoIds = Array.isArray(promotion.productIds)
      ? promotion.productIds.map((pid) => String(pid))
      : [];
    
    // Check if promotion applies to any items in cart (by productId or category)
    const matchesByProductId = targetItems.filter((item) => 
      promoIds.includes(String(item.id ?? item._id))
    );
    
    // Also check by category if no direct product matches
    let matchesByCategory = [];
    if (matchesByProductId.length === 0 && promotion.categories && Array.isArray(promotion.categories)) {
      const promotionCategories = promotion.categories.map(cat => String(cat).toLowerCase());
      matchesByCategory = targetItems.filter((item) => {
        const itemCategory = item.category ? String(item.category).toLowerCase() : null;
        return itemCategory && promotionCategories.includes(itemCategory);
      });
    }
    
    const matches = matchesByProductId.length > 0 ? matchesByProductId : matchesByCategory;
    
    if (matches.length === 0) {
      toast.warning('Promotion does not apply to the current selection');
      return;
    }

    dispatch(
      applyCoupon({
        code: promotion.title || promotion.id || 'PROMOTION',
        discount: promotion.discountType === 'percentage' ? Number(promotion.discount) || 0 : 0,
        discountAmount: promotion.discountType === 'amount' ? Number(promotion.discountAmount) || 0 : 0,
        discountType: promotion.discountType || 'percentage',
        productIds: promoIds,
        sellerId: promotion.sellerId,
        promotionId: promotion.id,
        promotionTitle: promotion.title,
      })
    );
    toast.success(`${promotion.title || 'Promotion'} applied successfully!`);
  };
  
  const CartItem = ({ item }) => {
    const isSelected = useSelector(selectIsItemSelected(item.itemKey));
    const [quantityInput, setQuantityInput] = React.useState(
      item.isLotBooking ? (item.uiLotCount || 1) : item.quantity
    );

    // For lot booking: compute available lots info for +/- controls
    const availableLots = item.isLotBooking
      ? (item.lots || []).filter(l => (parseInt(l.lotNos) || 0) > 0)
      : [];
    const numberOfAvailableLots = availableLots.length;
    const lotSftOfFn = (lot) => lot.lotSft
      ? parseFloat(lot.lotSft) || 0
      : ((parseFloat(lot.lotLength) || 0) * (parseFloat(lot.lotWidth) || 0) * (parseInt(lot.lotNos) || 0)) / 144;
    const availableSft = availableLots.reduce((s, l) => s + lotSftOfFn(l), 0);

    // Minimum slabs for regular items (from product minimumOrderQuantity); lot bookings keep their own rules
    const minQty = item.isLotBooking ? 1 : Math.max(1, parseInt(item.minOrderSlabs) || 1);

    // Update local state when item quantity changes from outside
    React.useEffect(() => {
      setQuantityInput(item.isLotBooking ? (item.uiLotCount || 1) : item.quantity);
    }, [item.quantity, item.uiLotCount, item.isLotBooking]);
    
    const handleQuantityInputChange = (event) => {
      const inputValue = event.target.value;
      const maxStock = item.stockQuantity || 999;

      if (inputValue === '') {
        setQuantityInput('');
        return;
      }

      const numericValue = parseInt(inputValue);

      if (isNaN(numericValue)) {
        return;
      }

      if (numericValue > 0) {
        setQuantityInput(numericValue);
      }
    };

    const handleQuantityBlur = () => {
      const maxStock = item.stockQuantity || 999;

      if (quantityInput === '' || quantityInput < minQty) {
        setQuantityInput(minQty);
        handleQuantityChange(item.itemKey, minQty);
        if (minQty > 1 && quantityInput !== '') toast.error(`Minimum order quantity is ${minQty} Nos`);
      } else if (quantityInput > maxStock) {
        setQuantityInput(maxStock);
        handleQuantityChange(item.itemKey, maxStock);
        toast.error(`Maximum available quantity is ${maxStock} ${item.priceUnit || 'units'}`);
      } else {
        handleQuantityChange(item.itemKey, quantityInput);
      }
    };
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
      >
        <Card 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: isSelected ? '2px solid #1976d2' : '1px solid #e5e5e5',
            transition: 'all 0.2s ease-in-out',
            bgcolor: isSelected ? '#f3f8ff' : 'white',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transform: 'translateY(-1px)',
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'auto 80px 1fr auto',
              gap: 3,
              alignItems: 'center'
            }}>
              {/* Selection Checkbox */}
              <Checkbox
                checked={isSelected}
                onChange={() => handleToggleItemSelection(item.itemKey)}
                color="primary"
                sx={{
                  '&:hover': {
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                  }
                }}
              />
              
              {/* Product Image */}
              <Box sx={{ position: 'relative' }}>
                {item.image ? (
                  <CardMedia
                    component="img"
                    sx={{
                      height: 80,
                      width: 80,
                      borderRadius: 2,
                      objectFit: 'cover',
                      border: '1px solid #e5e5e5',
                    }}
                    image={getFullImageUrl(item.image)}
                    alt={item.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <Box
                  sx={{
                    height: 80,
                    width: 80,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                  display: item.image ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  border: '1px solid #e5e5e5',
                  color: '#6c757d'
                }}
              >
                🏛️
              </Box>
            </Box>
            
            {/* Product Details */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem', color: '#2c3e50' }}>
                {item.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Chip 
                  label={item.category} 
                  size="small" 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    height: 24,
                    borderColor: '#e5e5e5'
                  }} 
                />
                {item.isLotBooking && (
                  <Chip 
                    label="Lot Booking" 
                    size="small" 
                    variant="filled" 
                    color="success"
                    sx={{ 
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      height: 24,
                      fontWeight: 600
                    }} 
                  />
                )}
                {item.isAdvanceBooking && !item.isLotBooking && (
                  <Chip 
                    label="Advance Booking" 
                    size="small" 
                    variant="filled" 
                    color="primary"
                    sx={{ 
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      height: 24,
                      fontWeight: 600
                    }} 
                  />
                )}
                {item.specifications && Object.keys(item.specifications).length > 0 && (
                  <Chip 
                    label="Custom" 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                    sx={{ 
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      height: 24
                    }} 
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Seller: {item.sellerName}
              </Typography>
              
              {/* Lot Booking Details */}
              {(item.isLotBooking || item.isAdvanceBooking) && (
                <Box sx={{ 
                  mt: 1, 
                  p: 1.5, 
                  bgcolor: '#f0f8ff', 
                  borderRadius: 1, 
                  border: '1px solid #2196f3' 
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                    {item.isLotBooking ? 'Lot Booking - Payment Split:' : 'Advance Booking Details:'}
                  </Typography>
                  
                  {/* Lot Size Information for Lot Bookings */}
                  {item.isLotBooking && (
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          Unit Price:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          ₹{item.originalPrice?.toLocaleString() || 'N/A'} / {item.priceUnit || 'unit'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          Lot Size:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {item.lotSft || 1}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderTop: '1px solid #e0e0e0', pt: 0.5 }}>
                        <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          Total Lot Price:
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          ₹{((item.originalPrice || 0) * (item.lotSft || 1)).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {item.paymentSplit ? (
                    <>
                      {(() => {
                        const totalPrice = (item.originalPrice || 0) * (item.lotSft || 1);
                        const advancePct = item.paymentSplit.advancePercentage || 10;
                        const remainingPct = item.paymentSplit.remainingPercentage || 90;
                        const advanceAmt = (totalPrice * advancePct) / 100;
                        const remainingAmt = totalPrice - advanceAmt;
                        return (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                Total Amount:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                ₹{totalPrice.toLocaleString()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="success.main" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                Advance ({advancePct}%):
                              </Typography>
                              <Typography variant="body2" color="success.main" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                ₹{advanceAmt.toLocaleString()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.85rem' }}>
                                Remaining ({remainingPct}%):
                              </Typography>
                              <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.85rem' }}>
                                ₹{remainingAmt.toLocaleString()}
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                      
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                        Pay advance now, remaining amount due upon delivery
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        Total Cost: ₹{item.originalPrice?.toLocaleString() || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="primary.main" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        Advance ({item.advancePercentage || 10}%): ₹{item.price?.toLocaleString() || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        Remaining (90%): ₹{((item.originalPrice || 0) - (item.price || 0)).toLocaleString()}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
            
            {/* Right Column - Perfect Alignment */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateRows: 'auto auto auto auto auto',
              gap: '10px',
              justifyItems: 'end',
              alignItems: 'center',
              minWidth: '200px'
            }}>
              {/* Price */}
              <Typography variant="h6" sx={{
                fontWeight: 700,
                fontSize: '1.2rem',
                color: '#2c3e50',
                textAlign: 'right'
              }}>
                {item.isLotBooking
                  ? (() => {
                      const totalLotPrice = Number(item.originalPrice || 0) * Number(item.lotSft || 1);
                      const advancePct = item.paymentSplit?.advancePercentage || 10;
                      const advanceAmt = (totalLotPrice * advancePct) / 100;
                      return `₹${advanceAmt.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                    })()
                  : `₹${getCartItemAmount(item).toFixed(2)}`}
              </Typography>

              {/* Unit calculation */}
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                textAlign: 'right'
              }}>
                {item.isLotBooking
                  ? (() => {
                      const advancePct = item.paymentSplit?.advancePercentage || 10;
                      return `Advance (${advancePct}%) of ₹${Number(item.originalPrice || 0).toFixed(2)} × ${Number(item.lotSft || 1).toFixed(4)} SFT`;
                    })()
                  : item.pricePerSft
                    ? `₹${Number(item.pricePerSft).toFixed(2)} per SFT`
                    : `₹${Number(item.discountedPrice || item.price).toFixed(2)} × ${item.quantity}`}
              </Typography>
              
              {/* GST text */}
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                textAlign: 'right'
              }}>
                +GST
              </Typography>

              {/* Selected SFT (regular items) */}
              {!item.isLotBooking && getCartItemSelectedSft(item) > 0 && (
                <Typography variant="body2" sx={{
                  color: '#e67e22',
                  fontWeight: 600,
                  textAlign: 'right'
                }}>
                  Selected SFT: {getCartItemSelectedSft(item).toFixed(2)} SFT
                </Typography>
              )}
              
              {/* Quantity controls */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                p: 0.5,
                border: '1px solid #e5e5e5'
              }}>
                <IconButton
                  onClick={() => {
                    if (item.isLotBooking) {
                      dispatch(updateLotBookingLotCount({ itemKey: item.itemKey, uiLotCount: (item.uiLotCount || 1) - 1 }));
                    } else {
                      handleQuantityChange(item.itemKey, item.quantity - 1);
                    }
                  }}
                  disabled={item.isLotBooking ? (item.uiLotCount || 1) <= 1 : item.quantity <= minQty}
                  size="small"
                  sx={{
                    minWidth: 32,
                    height: 32,
                    '&:hover': { bgcolor: 'primary.main', color: 'white' },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <TextField
                  value={quantityInput}
                  onChange={handleQuantityInputChange}
                  onBlur={handleQuantityBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  inputProps={{
                    min: minQty,
                    max: item.stockQuantity,
                    style: { 
                      textAlign: 'center', 
                      width: '40px',
                      padding: '4px 0',
                      fontSize: '0.9rem',
                      fontWeight: 600
                    },
                  }}
                  size="small"
                  variant="standard"
                  sx={{
                    '& .MuiInput-underline:before': { display: 'none' },
                    '& .MuiInput-underline:after': { display: 'none' },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { display: 'none' },
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (item.isLotBooking) {
                      dispatch(updateLotBookingLotCount({ itemKey: item.itemKey, uiLotCount: (item.uiLotCount || 1) + 1 }));
                    } else {
                      handleQuantityChange(item.itemKey, item.quantity + 1);
                    }
                  }}
                  disabled={item.isLotBooking ? (item.uiLotCount || 1) >= numberOfAvailableLots : item.quantity >= item.stockQuantity}
                  size="small"
                  sx={{
                    minWidth: 32,
                    height: 32,
                    '&:hover': { bgcolor: 'primary.main', color: 'white' },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
              
              {/* Max quantity */}
              <Typography variant="caption" sx={{
                color: 'text.secondary',
                textAlign: 'right'
              }}>
                Max: {item.stockQuantity}
              </Typography>

              {/* SFT info for lot bookings */}
              {item.isLotBooking && (
                <>
                  <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 600, textAlign: 'right' }}>
                    Selected SFT: {Number(item.lotSft || 0).toFixed(4)} SFT
                  </Typography>
                  {availableSft > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
                      Available SFT: {availableSft.toFixed(4)} SFT
                    </Typography>
                  )}
                </>
              )}

              {/* Delete button */}
              <IconButton
                onClick={() => handleRemoveItem(item.itemKey, item.name)}
                color="error"
                size="small"
                sx={{ 
                  '&:hover': { 
                    bgcolor: 'error.light', 
                    color: 'white',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

  const OrderSummary = () => (
    <Card 
      sx={{ 
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #e5e5e5',
        position: 'sticky', 
        top: 24,
        overflow: 'visible',
        bgcolor: 'white'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#2c3e50', fontSize: '1.3rem' }}>
          Order Summary
        </Typography>
        
        {/* Selected Items Count */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: '#6c757d', mb: 1 }}>
            Selected Items: {selectedItemsCount}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>
            Total Items: {cartItems.length}
          </Typography>
        </Box>
        
        {/* Price Breakdown */}
        <Box sx={{ mb: 3 }}>
          {applicablePromotions.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid rgba(34,197,94,0.3)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main', mb: 1 }}>
                Available Promotions
              </Typography>
              <Stack spacing={1}>
                {applicablePromotions.map((promotion) => {
                  const isApplied =
                    appliedCoupon &&
                    appliedCoupon.code === (promotion.title || promotion.id || 'PROMOTION');
                  return (
                    <Button
                      key={promotion.id}
                      variant={isApplied ? 'contained' : 'outlined'}
                      color="success"
                      size="small"
                      onClick={() => handleApplyPromotion(promotion)}
                      disabled={isApplied}
                      sx={{
                        justifyContent: 'space-between',
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      <span>{promotion.title || `Promotion ${promotion.discount || promotion.discountAmount || 0}${promotion.discountType === 'amount' ? '₹' : '%'}`}</span>
                      <span>
                        {promotion.discountType === 'amount'
                          ? `₹${promotion.discountAmount || 0} OFF`
                          : `${promotion.discount || 0}% OFF`}
                      </span>
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              Subtotal ({selectedItemsCount} items)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              ₹{selectedSubtotal.toLocaleString()}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              GST (18%)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              ₹{selectedGst.toLocaleString()}
            </Typography>
          </Box>
          
          {appliedCoupon && selectedItemsDiscount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                Promotion ({appliedCoupon.code})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                  -₹{selectedItemsDiscount.toFixed(2)}
                </Typography>
                <Button 
                  size="small" 
                  onClick={handleRemoveCoupon}
                  sx={{ 
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem'
                  }}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
              Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
              ₹{selectedTotal.toLocaleString()}
            </Typography>
          </Box>
        </Box>
        
        {/* Checkout Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleCheckout}
          disabled={selectedItemsCount === 0}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            bgcolor: selectedItemsCount === 0 ? '#ccc' : '#2C3E50',
            '&:hover': {
              bgcolor: selectedItemsCount === 0 ? '#ccc' : '#2C3E50',
            },
            '&:disabled': {
              bgcolor: '#ccc',
              color: '#666'
            }
          }}
        >
          {selectedItemsCount === 0 ? 'Select Items to Checkout' : `Checkout (${selectedItemsCount} items)`}
        </Button>
        
        {/* Security Features */}
        <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid #e5e5e5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Security sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 500, color: '#6c757d' }}>
              Secure Checkout
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LocalShipping sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 500, color: '#6c757d' }}>
              Shipping calculated at checkout
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
  
  if (cartItems.length === 0) {
    return (
      <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 6 }}>
        <Container maxWidth="lg">
          <Card sx={{ 
            textAlign: 'center', 
            p: 6,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #e5e5e5',
            bgcolor: 'white'
          }}>
            <ShoppingCart sx={{ fontSize: 80, color: '#6c757d', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#2c3e50' }}>
              Your cart is empty
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 400, mx: 'auto', color: '#6c757d' }}>
              Looks like you haven't added any items to your cart yet. Start shopping to fill it up!
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/products')}
              endIcon={<ArrowForward />}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Continue Shopping
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      bgcolor: '#f8f9fa', 
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      margin: 0,
      padding: 0
    }}>
      <Box sx={{ 
        width: '100%',
        maxWidth: '100vw',
        margin: 0,
        p: '20px' // 20px padding for breathing room
      }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link 
            component={RouterLink} 
            to="/" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: '#6c757d',
              '&:hover': { color: 'primary.main' },
              transition: 'color 0.2s ease-in-out'
            }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 500, color: '#2c3e50' }}>
            <ShoppingCart sx={{ mr: 0.5 }} fontSize="inherit" />
            Shopping Cart
          </Typography>
        </Breadcrumbs>
        
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          bgcolor: 'white',
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #e5e5e5'
        }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1, color: '#2c3e50' }}>
              Shopping Cart
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#6c757d' }}>
              {itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            color="error"
            onClick={handleClearCart}
            startIcon={<Delete />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: '#e5e5e5',
              color: '#dc3545',
              '&:hover': {
                bgcolor: '#dc3545',
                color: 'white',
                borderColor: '#dc3545',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(220, 53, 69, 0.2)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Clear Cart
          </Button>
        </Box>
        
        {/* Shopping Steps */}
        <Card sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #e5e5e5',
          bgcolor: 'white'
        }}>
          <Stepper activeStep={0} alternativeLabel>
            <Step>
              <StepLabel 
                icon={<ShoppingCart />}
                sx={{
                  '& .MuiStepLabel-label': { fontWeight: 600, color: '#2c3e50' },
                  '& .MuiStepIcon-root': { fontSize: '1.5rem' }
                }}
              >
                Shopping Cart
              </StepLabel>
            </Step>
            <Step>
              <StepLabel 
                icon={<LocalShipping />}
                sx={{
                  '& .MuiStepLabel-label': { fontWeight: 600, color: '#6c757d' },
                  '& .MuiStepIcon-root': { fontSize: '1.5rem' }
                }}
              >
                Shipping Details
              </StepLabel>
            </Step>
            <Step>
              <StepLabel 
                icon={<CreditCard />}
                sx={{
                  '& .MuiStepLabel-label': { fontWeight: 600, color: '#6c757d' },
                  '& .MuiStepIcon-root': { fontSize: '1.5rem' }
                }}
              >
                Payment
              </StepLabel>
            </Step>
          </Stepper>
        </Card>
        
        {/* CSS Grid Layout with 30px gap */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 350px', // Left column flexible, right column fixed 350px
          gap: '30px', // 30px gap between columns
          alignItems: 'start'
        }}>
          {/* Left Column - Cart Items (flexible width with internal padding) */}
          <Box sx={{ 
            pr: 2 // Right padding to prevent content from getting too close to gap
          }}>
            {/* Cart Items Header with Select All */}
            {cartItems.length > 0 && (
              <Card sx={{ 
                mb: 2, 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid #e5e5e5'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between' 
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allItemsSelected}
                          indeterminate={selectedItemsCount > 0 && !allItemsSelected}
                          onChange={handleSelectAll}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Select All Items ({cartItems.length})
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="text.secondary">
                      {selectedItemsCount} of {cartItems.length} selected
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {cartItems.map((item) => (
              <CartItem key={item.itemKey} item={item} />
            ))}
            
            {/* Continue Shopping */}
            <Card sx={{ 
              mt: 2, 
              textAlign: 'center',
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/products')}
                  startIcon={<ArrowForward sx={{ transform: 'rotate(180deg)' }} />}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>
          </Box>
          
          {/* Right Column - Order Summary (fixed 350px width with internal padding) */}
          <Box sx={{ 
            pl: 2 // Left padding for content breathing room
          }}>
            <OrderSummary />
          </Box>
        </Box>
        
        {/* Trust Indicators */}
        <Card sx={{ 
          mt: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #e5e5e5',
          bgcolor: '#f8f9fa'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4} sx={{ textAlign: 'center' }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid #e5e5e5',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Security sx={{ fontSize: 48, color: '#28a745', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#2c3e50' }}>
                    Secure Payment
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6c757d' }}>
                    Your payment information is processed securely
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid #e5e5e5',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                  }
                }}>
                  <LocalShipping sx={{ fontSize: 48, color: '#007bff', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#2c3e50' }}>
                    Fast Delivery
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6c757d' }}>
                    Quick and reliable delivery across India
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid #e5e5e5',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                  }
                }}>
                  <ShoppingCartCheckout sx={{ fontSize: 48, color: '#ffc107', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#2c3e50' }}>
                    Easy Returns
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6c757d' }}>
                    30-day return policy for your peace of mind
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default CartPage;